<template>
  <v-container fluid>
    <v-card>
      <v-toolbar color="primary" density="comfortable">
        <v-toolbar-title class="text-white"> Activités </v-toolbar-title>
        <v-spacer />
        <div class="d-flex ga-2">
          <ActionButton v-if="selectedTaskIds.length > 0" icon="mdi-link-plus" tooltip="Ajouter à une tournée"
            color="orange" @click="showAssignToTaskListDialog" />

          <TableHeaderButtons :attr="toolTipText" @add="add" @remove="remove" @reload="reloadData" @export="exportTable"
            @search="updateSearchPanel" :hasSelectedLines="selectedTaskIds.length > 0" />
        </div>
      </v-toolbar>
      <!-- Status Filters -->
      <v-row class="px-4 py-2" align="center" density="compact">
        <v-col cols="12">
          <div class="d-flex flex-wrap ga-4">
            <div class="text-caption text-medium-emphasis mb-2">Statut</div>
            <v-switch v-model="a_faire" label="ÀFaire" color="info" density="compact" inset hide-details />
            <v-switch v-model="en_cours" label="EnCours" color="primary" density="compact" inset hide-details />
            <v-switch v-model="assigned" label="Assigné" color="warning" density="compact" inset hide-details />
            <v-switch v-model="approuved" label="Approuvé" color="success" density="compact" inset hide-details />
          </div>
        </v-col>
      </v-row>

      <!-- Type Filters -->
      <v-row class="px-4 py-2" align="center" density="compact">
        <v-col cols="12">
          <div class="d-flex flex-wrap ga-4">
            <div class="text-caption text-medium-emphasis mb-2">Type</div>
            <v-switch v-model="panne" label="Panne" color="error" density="compact" inset hide-details />
            <v-switch v-model="caisse_maintenance" label="Caisse" color="warning" density="compact" inset
              hide-details />
            <v-switch v-model="releve_facturation" label="Facturation" color="info" density="compact" inset
              hide-details />
            <v-switch v-model="location_maintenance" label="Location" color="primary" density="compact" inset
              hide-details />
            <v-switch v-model="enlevement" label="Enlèvement" color="orange" density="compact" inset hide-details />
            <v-switch v-model="installation" label="Installation" color="green" density="compact" inset hide-details />
            <v-switch v-model="preparation" label="Préparation" color="teal" density="compact" inset hide-details />
          </div>
        </v-col>
      </v-row>
      <Table :loading="loading" :columns="columns" :searchField="true" :items="tasks" :showSelect="true" @edit="edit"
        @rowClicked="rowClicked" @selectionChanged="selectionChanged" />
    </v-card>

    <v-overlay :model-value="loading">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>

    <!-- Search panel -->
    <SearchPanel :rightDrawer="rightDrawer" @cancelSearch="cancelSearch" @searchData="searchTasks">
      <v-row>
        <v-col cols="11">
          <v-autocomplete clearable label="Type" :items="taskTypes" v-model="searchFilter.contain.taskType" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="11">
          <v-autocomplete clearable label="Status" :items="taskStatuses" v-model="searchFilter.contain.taskStatus" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="11">
          <v-autocomplete clearable label="Laverie" item-title="numeroDeContrat" item-value="id" :items="laveries"
            v-model="searchFilter.contain.laverieId" />
        </v-col>
      </v-row>
    </SearchPanel>

    <!-- Assign dialog -->
    <v-dialog :scrim="true" v-model="assignToTaskListModal" width="700" persistent>
      <v-card>
        <v-card-title>{{ assignModalTitle }}</v-card-title>
        <v-card-text>{{ assignModalText }}</v-card-text>

        <v-list>
          <v-radio-group v-model="selectedTaskList">
            <template v-if="taskListsEnCours.length > 0">
              <v-radio v-for="(item, i) in taskListsEnCours" :key="i" :value="item" :label="taskListLabel(item)" />
            </template>
            <v-alert v-else type="info" density="compact">
              Aucune tournée trouvée avec le status EN_COURS.
            </v-alert>
          </v-radio-group>
        </v-list>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" color="primary" @click="onAssignToTaskList">Confirm</v-btn>
          <v-btn variant="text" color="error" @click="cancelAssignToTaskList">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmDialog v-model="dialog" :dialogTitle="dialogTitle" :dialogText="dialogText" @confirm="onConfirm"
      @cancel="onCancel" confirm-button-text="Confirmer" cancel-button-text="Annuler" />
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";

import Table from "@/components/Table.vue";
import TableHeaderButtons from "@/components/TableHeaderButtons.vue";
import SearchPanel from "@/components/SearchPanel.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";

import { buildJsonServerQuery } from "@/utils/app-util";
import { useAppStore } from "@/store/app";
import { TableColumn, Task, getTaskExport } from "@/types";
import { TaskList } from "@/types";
import { TaskListStatus } from "@/types";
import { TaskStatus, TaskType } from "@/types";
import { Laverie } from "@/types";

import {
  deleteTask,
  searchForTasks,
  getFilteredTasks,
  getTaskById,
} from "@/utils/task-api";
import { assignTasksToList, getFilteredTaskLists } from "@/utils/tasklist-api";
import { getAllLaveries } from "@/utils/laverie-api";
import { createAndSaveDocument } from "@/utils/docx-util";
import ActionButton from "@/components/ActionButton.vue";

const router = useRouter();
const appStore = useAppStore();

const dialog = ref(false);
const assignToTaskListModal = ref(false);
const loading = ref(false);

const dialogTitle = "Suppression";
const dialogText = "Voulez-vous supprimer les activités sélectionnées?";
const assignModalTitle = "Selection";
const assignModalText =
  "Sélectionnez une tournée puis confirmez pour rajouter les activités à cette tournée";

const showSearchPanel = ref(false);
const tasks = ref<Task[]>([]);
const selectedTaskIds = ref<string[]>([]);
const taskListsEnCours = ref<TaskList[]>([]);
const selectedTaskList = ref<TaskList | null>(null);
const laveries = ref<Laverie[]>([]);

const a_faire = ref(true);
const en_cours = ref(false);
const assigned = ref(false);
const approuved = ref(false);

const panne = ref(true);
const caisse_maintenance = ref(false);
const releve_facturation = ref(false);
const location_maintenance = ref(false);
const enlevement = ref(false);
const installation = ref(false);
const preparation = ref(false);

const columns: TableColumn[] = [
  { title: "Type", key: "taskType" },
  { title: "Sujet", key: "subject" },
  { title: "Laverie", key: "name" },
  { title: "Contrat", key: "numeroDeContrat" },
  { title: "Code Tournée", key: "taskListCodeTournee" },
  { title: "Status", key: "taskStatus" },
  { title: "", key: "actions", sortable: false },
];

const searchFilter = ref({
  contain: {
    taskType: null as TaskType | null,
    taskStatus: null as TaskStatus | null,
    laverieId: null as string | null,
  },
});

const toolTipText = computed(() => "une activité");
const taskTypes = computed(() => Object.values(TaskType));
const taskStatuses = computed(() => Object.values(TaskStatus));

const rightDrawer = computed({
  get: () => showSearchPanel.value,
  set: (v) => {
    showSearchPanel.value = v;
    fetchLaveries();
  },
});

watch(
  [
    a_faire,
    en_cours,
    assigned,
    approuved,
    panne,
    caisse_maintenance,
    releve_facturation,
    location_maintenance,
    enlevement,
    installation,
    preparation,
  ],
  () => {
    saveFiltersToLocalStorage();
    fetchTasks();
  }
);

function taskListLabel(t: TaskList) {
  return t.technicienNames
    ? `${t.codeTournee} [ ${t.taskListStatus} ] [ ${t.technicienNames} ]`
    : `${t.codeTournee} [ ${t.taskListStatus} ]`;
}

function showAssignToTaskListDialog() {
  assignToTaskListModal.value = true;
}

function updateSearchPanel() {
  rightDrawer.value = !rightDrawer.value;
}

const onAssignToTaskList = async () => {
  if (loading.value) {
    return;
  }

  const taskList = selectedTaskList.value;
  const taskIds = [...selectedTaskIds.value];

  if (!taskList || taskIds.length === 0) {
    return;
  }

  try {
    loading.value = true;

    await assignTasksToList(taskList.id, taskIds);

    selectedTaskIds.value = [];
    selectedTaskList.value = null;
    assignToTaskListModal.value = false;
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
};

function cancelAssignToTaskList() {
  assignToTaskListModal.value = false;
}

function selectionChanged(selected: string[]) {
  selectedTaskIds.value = [...selected];
}

function rowClicked(item: Task) {
  router.push(`tasks/${item.id}`);
}

function edit(item: Task) {
  router.push(`tasks/${item.id}`);
}

function add() {
  router.push("NewTask");
}

function remove() {
  dialog.value = true;
}

async function onConfirm() {
  dialog.value = false;
  try {
    loading.value = true;
    await deleteTask(selectedTaskIds.value);
    fetchTasks();
    selectedTaskIds.value = [];
    appStore.sendSuccessNotice("Task has been deleted.");
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

function onCancel() {
  dialog.value = false;
}

function searchTasks() {
  const query = buildJsonServerQuery(searchFilter.value);
  _searchTasks(query);
  showSearchPanel.value = false;
}

async function _searchTasks(query: string) {
  loading.value = true;
  try {
    tasks.value = await searchForTasks(query);
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

function reloadData() {
  fetchTasks();
}

const exportTable = async () => {
  if (loading.value) {
    return;
  }

  const taskIds = [...selectedTaskIds.value];
  if (taskIds.length === 0) {
    return;
  }

  try {
    loading.value = true;
    const selectedTasks = await Promise.all(taskIds.map((id) => getTaskById(id)));

    createAndSaveDocument(
      "export_activites",
      "../templates/ExportActivitesTemplate.docx",
      getTaskExport(selectedTasks)
    );
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
};

function cancelSearch() {
  showSearchPanel.value = false;
}

async function fetchTasks() {
  loading.value = true;
  try {
    const statuses: TaskStatus[] = [];
    if (a_faire.value) statuses.push(TaskStatus.A_FAIRE);
    if (en_cours.value) statuses.push(TaskStatus.EN_COURS);
    if (assigned.value) statuses.push(TaskStatus.ASSIGNE);
    if (approuved.value) statuses.push(TaskStatus.APPROUVE);

    const types: TaskType[] = [];
    if (panne.value) types.push(TaskType.PANNE);
    if (caisse_maintenance.value) types.push(TaskType.CAISSE_ET_MAINTENANCE);
    if (releve_facturation.value) types.push(TaskType.RELEVE_FACTURATION);
    if (location_maintenance.value) types.push(TaskType.LOCATION_MAINTENANCE);
    if (enlevement.value) types.push(TaskType.ENLEVEMENT);
    if (installation.value) types.push(TaskType.INSTALLATION);
    if (preparation.value) types.push(TaskType.PREPARATION);

    tasks.value =
      statuses.length || types.length ? await getFilteredTasks(statuses, types) : [];
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

async function fetchLaveries() {
  laveries.value = await getAllLaveries();
}

function saveFiltersToLocalStorage() {
  localStorage.setItem(
    "taskTableFilters",
    JSON.stringify({
      a_faire: a_faire.value,
      en_cours: en_cours.value,
      assigned: assigned.value,
      approuved: approuved.value,
      panne: panne.value,
      caisse_maintenance: caisse_maintenance.value,
      releve_facturation: releve_facturation.value,
      location_maintenance: location_maintenance.value,
      enlevement: enlevement.value,
      installation: installation.value,
      preparation: preparation.value,
    })
  );
}

function loadFiltersFromLocalStorage() {
  const saved = localStorage.getItem("taskTableFilters");
  if (!saved) return;
  try {
    Object.assign(
      {
        a_faire,
        en_cours,
        assigned,
        approuved,
        panne,
        caisse_maintenance,
        releve_facturation,
        location_maintenance,
        enlevement,
        installation,
        preparation,
      },
      JSON.parse(saved)
    );
  } catch {
    /* ignore */
  }
}

onMounted(async () => {
  loadFiltersFromLocalStorage();
  await fetchTasks();
  taskListsEnCours.value = await getFilteredTaskLists([TaskListStatus.EN_COURS]);
});
</script>
<style scoped>
.title {
  font-size: 1.5rem;
  font-weight: bold;
}
</style>
